import React, { useState, useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { useFormik, FormikProvider } from 'formik';
import { Box, Button } from '@mui/material';
import * as yup from 'yup';
import _ from 'lodash';
import * as productApi from '../../lib/api/product';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import {
  unloadProduct,
  viewProduct,
} from '../../modules/productModule/product';
import TitleBox from '../../components/common/breadcrumbs/TitleBox';
import DetailTemplate from '../../components/common/template/DetailTemplate';
import ProductTemplate from '../../components/productmng/ProductTemplate';
import ProductForm from '../../components/productmng/ProductForm';

const ProductEditContainer = () => {
  const { id } = useParams();
  let navigate = useNavigate();
  const [titleData, setTitleData] = useState(null);
  //처음 마운트 될 때 partenr 데이터 요청
  const dispatch = useDispatch();

  const { product, error, loading } = useSelector(
    ({ productModule, loading }) => ({
      product: productModule.product.product,
      error: productModule.product.error,
      loading: loading['product/VIEW_PRODUCT'],
    }),
  );
  const { enqueueSnackbar } = useSnackbar();

  const handleGoBack = () => {
    navigate(`/productmng/${id}`);
  };
  const [initialValues, setInitialValues] = useState({
    //itemCd: '',
    productLine: '',
    productNm: '',
    partnerUuid: '',
    partnerNm: '',
    purchasePrices: [],
    priceConsume: '',
    category: '',
    productUnit: '',
    productDesc: '',
    useYn: true,
  });

  useEffect(() => {
    dispatch(viewProduct(id));

    //언마운트될때 리덕스에서 파트너 데이터 초기화
    return () => {
      dispatch(unloadProduct());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (product) {
      let title = {
        name: `[${product.productLine}] ${product.productNm}`,
        no: product.prouductUuid,
      };
      setTitleData(title);
      const copyProduct = _.clone(product);
      copyProduct.useYn = copyProduct.useYn === 'Y' ? true : false;
      copyProduct.purchasePrices = product.purchasePrices || [];

      setInitialValues(copyProduct);
    }
  }, [product]);

  const validationSchema = yup.object({
    productNm: yup.string('').required('제품명을 입력하세요'),
    productSpec: yup.string().required('제품규격을 입력하세요'),
    productLine: yup.string().required('제품군을 입력하세요'),
    partnerNm: yup.string().required('제조사를 선택하세요'),
    purchasePrices: yup
      .array()
      .of(
        yup.object({
          partnerUuid: yup.string().required('매입처를 선택하세요'),
          priceVal: yup
            .number()
            .typeError('매입가는 숫자만 가능합니다.')
            .required('매입가를 입력하세요')
            .min(0, '최소 0보다 큰값이어야 합니다'),
        }),
      )
      .min(1, '매입처별 매입가를 1건 이상 등록하세요'),
    priceConsume: yup
      .number()
      .typeError('소비자 가격은 숫자만 가능합니다.')
      .positive()
      .nullable()
      .min(0, '최소 0보다 큰값이어야 합니다'),
    category: yup.string().required('분류 코드를 선택하세요'),
    useYn: yup.boolean(),
  });

  const onSave = async (dataToSubmit) => {
    formik.setSubmitting(true);
    try {
      const res = await productApi.editProduct(id, dataToSubmit);
      if (res.data.info) {
        navigate(`/productmng/${res.data.info.productUuid}`);
        enqueueSnackbar('제품정보가 저장되었습니다', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    } finally {
      formik.resetForm();
      formik.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      let dataToSubmit = {
        productUuid: id,
        prdTyp1: '-',
        prdTyp2: '-',
        itemCd: values.category,
        productLine: values.productLine.trim(),
        productSpec: values.productSpec.trim(),
        productNm: values.productNm.trim(),
        modelNm: values.productNm.trim(), //모델명 제거-productNm 동일하게 넣음.
        partnerUuid: values.partnerUuid,
        category: values.category,
        purchasePrices: values.purchasePrices,
        priceConsume: values.priceConsume,
        productUnit: values.productUnit,
        productDesc: values.productDesc,
        useYn: values.useYn ? 'Y' : 'N',
      };

      // console.log(dataToSubmit);
      onSave(dataToSubmit);
    },
  });

  return (
    <ProductTemplate product={product} loading={loading} error={error}>
      <TitleBox
        handleGoBack={handleGoBack}
        menuName="제품관리/제품 관리/수정"
        menuLink="/productmng"
        icon="product"
        titleData={titleData}
      />
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mt: 1,
          px: 2,
        }}
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            my: 2,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
          >
            저장
          </Button>
        </Box>
        <DetailTemplate title="기본정보">
          <ProductForm formik={formik} />
        </DetailTemplate>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            my: 2,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
          >
            저장
          </Button>
        </Box>
      </Box>
    </ProductTemplate>
  );
};

export default ProductEditContainer;
